const DEFAULT_TTS_OPTIONS = {
  locale: "ja-JP",
  rate: 1,
  pitch: 1,
  cancel: true,
};

const IOS_TTS_OPTIONS = {
  ...DEFAULT_TTS_OPTIONS,
  // iOS AVSpeechUtterance.rate and Android TextToSpeech rate are not comparable.
  // This value is tuned to be closer to Android's current playback speed.
  rate: 0.45,
  pitch: 1,
};

const ANDROID_TTS_OPTIONS = {
  ...DEFAULT_TTS_OPTIONS,
  // Slightly lower pitch makes Japanese playback sound less sharp on many Android engines.
  pitch: 0.92,
};

function isCordovaRuntime() {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    typeof cordova !== "undefined" &&
    typeof cordova.exec === "function"
  );
}

function getAdvancedTtsPlugin() {
  if (typeof window === "undefined") {
    return null;
  }

  return window.TTS && typeof window.TTS.speak === "function"
    ? window.TTS
    : null;
}

function getRuntimePlatform() {
  if (typeof window === "undefined") {
    return "unknown";
  }

  if (typeof device !== "undefined" && typeof device.platform === "string") {
    const platform = device.platform.toLowerCase();
    if (platform.includes("ios")) {
      return "ios";
    }
    if (platform.includes("android")) {
      return "android";
    }
  }

  const href = String(window.location.href || "").toLowerCase();
  if (href.includes("ios")) {
    return "ios";
  }
  if (href.includes("android")) {
    return "android";
  }

  return "unknown";
}

function getTtsOptions() {
  const platform = getRuntimePlatform();

  if (platform === "ios") {
    return IOS_TTS_OPTIONS;
  }
  if (platform === "android") {
    return ANDROID_TTS_OPTIONS;
  }

  return DEFAULT_TTS_OPTIONS;
}

function speakByAdvancedTts(text) {
  const plugin = getAdvancedTtsPlugin();
  if (!plugin) {
    return Promise.reject(new Error("TTS advanced plugin is unavailable"));
  }

  return plugin
    .speak({
      text,
      ...getTtsOptions(),
    })
    .then(() => true);
}

function stopByAdvancedTts() {
  const plugin = getAdvancedTtsPlugin();
  if (!plugin || typeof plugin.stop !== "function") {
    return Promise.resolve(false);
  }

  return plugin.stop().then(() => true).catch(() => false);
}

function speakByWebSpeech(text) {
  if (
    typeof window === "undefined" ||
    typeof window.speechSynthesis === "undefined" ||
    typeof SpeechSynthesisUtterance === "undefined"
  ) {
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    try {
      const ttsOptions = getTtsOptions();
      const speech = new SpeechSynthesisUtterance(text);
      speech.lang = ttsOptions.locale;
      speech.pitch = ttsOptions.pitch;
      speech.rate = ttsOptions.rate;
      speech.volume = 1;

      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(speech);
      resolve(true);
    } catch (error) {
      console.warn("Web Speech playback failed.", error);
      resolve(false);
    }
  });
}

export async function speakText(text) {
  const normalizedText = String(text || "").trim();
  const advancedTtsPlugin = getAdvancedTtsPlugin();

  if (!normalizedText) {
    return false;
  }

  if (advancedTtsPlugin && isCordovaRuntime()) {
    try {
      return await speakByAdvancedTts(normalizedText);
    } catch (error) {
      console.warn("TTS advanced plugin playback failed.", error);
    }
  }

  return speakByWebSpeech(normalizedText);
}

export function stopSpeakText() {
  if (
    typeof window !== "undefined" &&
    typeof window.speechSynthesis !== "undefined"
  ) {
    window.speechSynthesis.cancel();
  }

  if (getAdvancedTtsPlugin() && isCordovaRuntime()) {
    stopByAdvancedTts();
  }
}
